import { useQuery } from "@tanstack/react-query";
import { Link, useParams } from "@tanstack/react-router";
import { ArrowLeft, BadgeCheck, Loader2 } from "lucide-react";
import type { ReactNode } from "react";
import { ApiError, fetchListing, formatBytes, type StoreListing } from "@/api/store.api";
import { cn } from "@/lib/utils";
import { GetAppButton } from "./get-app-button";
import { hardwareIcon, hardwareLabel, permissionIcon, permissionLabel, tagLabel, typeLabel } from "./labels";
import { ListingIcon } from "./listing-icon";
import { StoreShell } from "./store-shell";

/** The listing for the current route, shared with the app view. */
export function useRouteListing() {
  const { packageName } = useParams({ from: "/$packageName" });
  const listing = useQuery({
    queryKey: ["listing", packageName],
    queryFn: () => fetchListing(packageName),
    retry: (count, error) => !(error instanceof ApiError && error.status === 404) && count < 1,
  });
  return { packageName, listing };
}

export function ListingPage() {
  const { packageName, listing } = useRouteListing();

  return (
    <StoreShell>
      <Link to="/" className="inline-flex items-center gap-1 text-sm font-medium text-[#68746d] hover:text-[#111217]">
        <ArrowLeft className="size-4" />
        Store
      </Link>
      {listing.isPending ? (
        <div className="flex items-center gap-2 py-16 text-sm text-[#747780]">
          <Loader2 className="size-4 animate-spin" />
          Loading…
        </div>
      ) : listing.isError ? (
        <div className="py-16 text-center">
          <p className="font-medium">
            {listing.error instanceof ApiError && listing.error.status === 404 ? "This miniapp is not in the catalog." : "Couldn't load this miniapp."}
          </p>
          <p className="mt-1 font-mono text-xs text-[#8a8d95]">{packageName}</p>
        </div>
      ) : (
        <ListingBody listing={listing.data} />
      )}
    </StoreShell>
  );
}

function ListingBody({ listing }: { listing: StoreListing }) {
  const required = listing.permissions.filter(p => p.required !== false);
  const optional = listing.permissions.filter(p => p.required === false);
  return (
    <article className="mt-5 space-y-8">
      <header className="flex flex-col gap-5 sm:flex-row sm:items-start">
        <ListingIcon listing={listing} size={96} className="shadow-[0_1px_2px_rgba(20,21,27,0.08)]" />
        <div className="min-w-0 flex-1">
          <h1 className="font-display text-3xl font-bold leading-9">{listing.name}</h1>
          <p className="mt-1 flex items-center gap-1.5 text-sm text-[#5d6068]">
            {listing.author.name}
            {listing.author.verified ? <BadgeCheck className="size-4 text-[#087d50]" aria-label="Verified publisher" /> : null}
          </p>
          {listing.tagline ? <p className="mt-2 text-base text-[#14151b]">{listing.tagline}</p> : null}
          <div className="mt-4 flex flex-wrap items-center gap-3">
            <GetAppButton size="lg" />
            {listing.minHostVersion ? <span className="text-xs text-[#8a8d95]">Needs Glassly App {listing.minHostVersion} or newer</span> : null}
          </div>
          <div className="mt-4 flex flex-wrap gap-2">
            <Chip>{typeLabel(listing.type)}</Chip>
            <Chip>{formatBytes(listing.bundleSizeBytes)}</Chip>
            {listing.tags.map(tag => (
              <Chip key={tag} className="capitalize">
                {tagLabel(tag)}
              </Chip>
            ))}
          </div>
        </div>
      </header>

      {listing.screenshots.length > 0 ? (
        <Section title="On your glasses" subtitle="What the display shows while this miniapp runs">
          <div className="-mx-4 flex snap-x gap-3 overflow-x-auto px-4 pb-1 [scrollbar-width:none] sm:mx-0 sm:px-0 [&::-webkit-scrollbar]:hidden">
            {listing.screenshots.map((shot, index) => (
              <img
                key={shot.url}
                src={shot.url}
                alt={`${listing.name} screenshot ${index + 1}`}
                loading="lazy"
                style={{ aspectRatio: shot.width && shot.height ? `${shot.width} / ${shot.height}` : "2 / 1" }}
                className="w-[74%] max-w-[480px] shrink-0 snap-start rounded-2xl border border-[#e4e6e2] bg-black object-contain sm:w-[48%]"
              />
            ))}
          </div>
        </Section>
      ) : null}

      <Section title="About">
        <p className="whitespace-pre-line text-[15px] leading-6 text-[#2b2c33]">{listing.description || "No description yet."}</p>
      </Section>

      <Section title="Requested at launch" subtitle="Asked when you launch it.">
        {listing.permissions.length === 0 ? (
          <p className="text-sm text-[#747780]">This miniapp does not ask for any phone permissions.</p>
        ) : (
          <ul className="divide-y divide-[#eceeeb] rounded-2xl border border-[#e4e6e2] bg-white">
            {[...required, ...optional].map((permission, index) => {
              const Icon = permissionIcon(permission.type);
              return (
                <li key={`${permission.type}-${index}`} className="flex items-start gap-3 px-4 py-3">
                  <Icon className="mt-0.5 size-4 shrink-0 text-[#5d6068]" />
                  <div className="min-w-0 flex-1">
                    <div className="flex items-center gap-2 text-sm font-medium">
                      {permissionLabel(permission.type)}
                      {permission.required === false ? <span className="text-xs font-normal text-[#8a8d95]">Optional</span> : null}
                    </div>
                    {permission.description ? <p className="mt-0.5 text-[13px] text-[#747780]">{permission.description}</p> : null}
                  </div>
                </li>
              );
            })}
          </ul>
        )}
      </Section>

      {listing.hardwareRequirements.length > 0 ? (
        <Section title="Glasses hardware">
          <ul className="divide-y divide-[#eceeeb] rounded-2xl border border-[#e4e6e2] bg-white">
            {listing.hardwareRequirements.map((requirement, index) => {
              const Icon = hardwareIcon(requirement.type);
              return (
                <li key={`${requirement.type}-${index}`} className="flex items-start gap-3 px-4 py-3">
                  <Icon className="mt-0.5 size-4 shrink-0 text-[#5d6068]" />
                  <div className="min-w-0 flex-1">
                    <div className="flex items-center gap-2 text-sm font-medium">
                      {hardwareLabel(requirement.type)}
                      <span className="text-xs font-normal text-[#8a8d95]">{requirement.level.toUpperCase() === "REQUIRED" ? "Required" : "Optional"}</span>
                    </div>
                    {requirement.description ? <p className="mt-0.5 text-[13px] text-[#747780]">{requirement.description}</p> : null}
                  </div>
                </li>
              );
            })}
          </ul>
        </Section>
      ) : null}

      <Section title="Details">
        <dl className="grid grid-cols-[auto_1fr] gap-x-6 gap-y-2 rounded-2xl border border-[#e4e6e2] bg-white px-4 py-3 text-sm">
          <Row label="Publisher">
            {listing.author.name}
            {listing.author.verified ? <span className="ml-2 text-xs text-[#087d50]">Verified publisher</span> : null}
          </Row>
          <Row label="Version">{listing.version}</Row>
          <Row label="Download size">{formatBytes(listing.bundleSizeBytes)}</Row>
          <Row label="Type">{typeLabel(listing.type)}</Row>
          {listing.tags.length > 0 ? <Row label="Tags">{listing.tags.map(tagLabel).join(", ")}</Row> : null}
          <Row label="Package">
            <span className="font-mono text-[13px]">{listing.packageName}</span>
          </Row>
          <Row label="Updated">{new Date(listing.updatedAt).toLocaleDateString()}</Row>
        </dl>
      </Section>
    </article>
  );
}

function Section({ title, subtitle, children }: { title: string; subtitle?: string; children: ReactNode }) {
  return (
    <section>
      <h2 className="font-display text-lg font-bold">{title}</h2>
      {subtitle ? <p className="mb-3 text-sm text-[#747780]">{subtitle}</p> : <div className="mb-3" />}
      {children}
    </section>
  );
}

function Row({ label, children }: { label: string; children: ReactNode }) {
  return (
    <>
      <dt className="text-[#747780]">{label}</dt>
      <dd className="min-w-0 break-words">{children}</dd>
    </>
  );
}

function Chip({ children, className }: { children: ReactNode; className?: string }) {
  return <span className={cn("rounded-full bg-[#eceeeb] px-2.5 py-1 text-xs font-medium text-[#2b2c33]", className)}>{children}</span>;
}
